// Scene decoding + the drag-a-box interaction. The comparison UX lives in
// studio.ts and all compositing math in raster.ts — this module keeps the
// shared geotiff decode (all spectral bands into a RawScene) and the map
// gesture that produces a bbox.

import { fromArrayBuffer } from 'geotiff';
import * as maplibregl from 'maplibre-gl';
import { toast } from './hud';
import { SCALE, rgbaToScene, type RawScene } from './raster';

// --- decoding ---------------------------------------------------------------

/** Decode every band named in `bandOrder` (+ dataMask) from a UINT16 GeoTIFF.
 *  Works for both the 11-band optical scene and the Sentinel-1 SAR scene
 *  (VV/VH/dataMask); for SAR it also derives a VV/VH ratio band for the
 *  false-colour view. */
export async function decodeAllBands(buf: ArrayBuffer,
                                     bandOrder: string[]): Promise<RawScene> {
  const tiff = await fromArrayBuffer(buf);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const present = bandOrder.filter((b) => b !== 'dataMask');
  const hasMask = bandOrder.includes('dataMask');
  const samples = present.map((b) => bandOrder.indexOf(b));
  if (hasMask) samples.push(bandOrder.indexOf('dataMask'));
  const rasters = await image.readRasters({ samples }) as unknown as Uint16Array[];
  const bands: RawScene['bands'] = {};
  present.forEach((b, i) => { bands[b] = rasters[i]!; });
  if (hasMask) {
    bands.dataMask = rasters[present.length]!;
  } else {
    const mask = new Uint16Array(width * height);
    mask.fill(SCALE);
    bands.dataMask = mask;
  }
  if (bands.VV && bands.VH) {
    const vv = bands.VV, vh = bands.VH;
    const ratio = new Float32Array(width * height);
    for (let i = 0; i < ratio.length; i++) ratio[i] = vv[i]! / (vh[i]! + 1);
    bands.ratio = ratio;
  }
  return { width, height, bands };
}

/** A rendered image (the NOAA VIIRS scenes GIBS serves as PNG) as a RawScene.
 *  No colour-space conversion or premultiplication, so the bytes come back as
 *  published. */
export async function decodeImageBands(buf: ArrayBuffer,
                                       blackIsNoData = true): Promise<RawScene> {
  const bmp = await createImageBitmap(new Blob([buf], { type: 'image/png' }),
    { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  return rgbaToScene(data, canvas.width, canvas.height, blackIsNoData);
}

// --- box draw ---------------------------------------------------------------

let drawCleanup: (() => void) | null = null;

// The live rubber-band preview: a client GeoJSON source + fill/outline layers
// added on demand and torn down when the gesture ends, so the operator can see
// the box they're dragging.
const PREVIEW_SRC = 'scan-draw-preview';
const PREVIEW_FILL = 'scan-draw-preview-fill';
const PREVIEW_LINE = 'scan-draw-preview-line';

function rectFeature(a: maplibregl.LngLat, b: maplibregl.LngLat): GeoJSON.Feature {
  const [w, e] = [Math.min(a.lng, b.lng), Math.max(a.lng, b.lng)];
  const [s, n] = [Math.min(a.lat, b.lat), Math.max(a.lat, b.lat)];
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]],
    },
  };
}

function ensurePreviewLayers(map: maplibregl.Map): void {
  if (map.getSource(PREVIEW_SRC)) return;
  map.addSource(PREVIEW_SRC, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: PREVIEW_FILL, type: 'fill', source: PREVIEW_SRC,
    paint: { 'fill-color': '#55d6be', 'fill-opacity': 0.15 },
  });
  map.addLayer({
    id: PREVIEW_LINE, type: 'line', source: PREVIEW_SRC,
    paint: { 'line-color': '#55d6be', 'line-width': 2, 'line-dasharray': [2, 2] },
  });
}

function removePreviewLayers(map: maplibregl.Map): void {
  if (map.getLayer(PREVIEW_LINE)) map.removeLayer(PREVIEW_LINE);
  if (map.getLayer(PREVIEW_FILL)) map.removeLayer(PREVIEW_FILL);
  if (map.getSource(PREVIEW_SRC)) map.removeSource(PREVIEW_SRC);
}

export function cancelScanDraw(map: maplibregl.Map): void {
  drawCleanup?.();
  drawCleanup = null;
  map.getCanvas().style.cursor = '';
}

// Pointer events, not MapLibre's mouse* events: MapLibre raises `mousedown` /
// `mousemove` / `mouseup` for a mouse only, so a finger never started the drag
// and the Studio was simply unreachable on a touch device. One pointer stream
// covers mouse, pen and touch, and pointer capture keeps the gesture alive if
// the finger leaves the canvas.
export function startScanDraw(map: maplibregl.Map,
                              onBox: (bbox: [number, number, number, number]) => void,
                              onDone: () => void): void {
  cancelScanDraw(map);

  // A scan area is a NORTH-UP geographic box — /api/scan takes a bbox and the
  // Studio frames one, so a rotated footprint is not expressible. On a camera
  // carrying bearing the band comes out tilted; carrying pitch it comes out
  // trapezoidal, with the far edge tens of kilometres away. Either way the
  // rectangle you drag is not the box you get, and the drag is the only thing
  // the operator can see. So level the camera, say so, and put the view back
  // when the gesture ends: "3D flight view" tilts to 55°, and permanently
  // losing that to draw one box is not the trade.
  const tilted = Math.abs(map.getBearing()) > 0.5 || map.getPitch() > 0.5;
  const restore = tilted
    ? { bearing: map.getBearing(), pitch: map.getPitch() } : null;
  toast(tilted
    ? 'Levelled the map — a scan box is north-up. Drag a box over the area; '
      + 'your view comes back after.'
    : 'Drag a box over the area to compare.');

  const canvas = map.getCanvas();
  canvas.style.cursor = 'crosshair';
  map.dragPan.disable();

  // At the browser level too: without this a touch-drag is claimed as a page
  // gesture (pan/zoom) before MapLibre or we ever see it.
  const priorTouchAction = canvas.style.touchAction;
  canvas.style.touchAction = 'none';

  const at = (ev: PointerEvent): maplibregl.LngLat => {
    const rect = canvas.getBoundingClientRect();
    return map.unproject([ev.clientX - rect.left, ev.clientY - rect.top]);
  };

  const arm = (): void => {
    let start: maplibregl.LngLat | null = null;
    let pointerId: number | null = null;

    const onDown = (ev: PointerEvent) => {
      if (pointerId !== null) return;      // ignore a second finger mid-drag
      pointerId = ev.pointerId;
      canvas.setPointerCapture(ev.pointerId);
      start = at(ev);
      ensurePreviewLayers(map);
    };
    const onMove = (ev: PointerEvent) => {
      if (!start || ev.pointerId !== pointerId) return;
      (map.getSource(PREVIEW_SRC) as maplibregl.GeoJSONSource | undefined)
        ?.setData(rectFeature(start, at(ev)));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const from = start;
      const to = at(ev);
      cancelScanDraw(map);
      if (!from) return;
      const box: [number, number, number, number] = [
        Math.min(from.lng, to.lng), Math.min(from.lat, to.lat),
        Math.max(from.lng, to.lng), Math.max(from.lat, to.lat)];
      if (box[2] - box[0] < 0.002 || box[3] - box[1] < 0.002) {
        toast('Box too small — drag a larger area.');
        onDone();
        return;
      }
      onBox(box);
      onDone();
    };
    // A cancelled pointer (a system gesture taking over) must not leave the map
    // stuck with dragPan disabled and a crosshair cursor.
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      cancelScanDraw(map);
      onDone();
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);

    drawCleanup = () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      if (pointerId !== null && canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
      canvas.style.touchAction = priorTouchAction;
      removePreviewLayers(map);
      map.dragPan.enable();
      if (restore) map.easeTo({ ...restore, duration: 300 });
    };
  };

  if (!restore) {
    arm();
    return;
  }
  map.easeTo({ bearing: 0, pitch: 0, duration: 350 });
  // Arm only once the ground has stopped moving under the cursor: a corner
  // taken mid-animation lands wherever the camera happened to be that frame.
  map.once('moveend', arm);
  drawCleanup = () => {
    map.off('moveend', arm);
    canvas.style.touchAction = priorTouchAction;
    map.dragPan.enable();
    map.easeTo({ ...restore, duration: 300 });
  };
}
